import { parseAndProfileCsv } from './disclosure_core.js';

export const MAX_SOURCE_BYTES = 5 * 1024 * 1024;
export const ROW_NUMBER_KEY = '@row';
export const CSV_TYPES = new Set(['text/csv', 'application/csv', 'application/vnd.ms-excel', 'text/plain']);

export function validateSourceDraft(input) {
  const id = String(input?.id ?? '').trim();
  if (!/^[a-z0-9][a-z0-9-]*$/.test(id)) throw Object.assign(new Error('Source ID must use lowercase letters, numbers, and hyphens.'), { status: 400 });
  const label = String(input?.label ?? '').trim();
  if (!label) throw Object.assign(new Error('Source label is required.'), { status: 400 });
  const fileName = String(input?.fileName ?? 'source.csv');
  if (!fileName.toLowerCase().endsWith('.csv')) throw Object.assign(new Error('Choose a .csv file.'), { status: 415 });
  const mediaType = String(input?.mediaType ?? 'text/csv').split(';')[0].trim().toLowerCase();
  if (mediaType && !CSV_TYPES.has(mediaType)) throw Object.assign(new Error(`Unsupported source type: ${mediaType}`), { status: 415 });
  const csvText = String(input?.csvText ?? '');
  const size = Buffer.byteLength(csvText, 'utf8');
  if (!size) throw Object.assign(new Error('The CSV is empty.'), { status: 400 });
  if (size > MAX_SOURCE_BYTES) throw Object.assign(new Error(`CSV exceeds the ${MAX_SOURCE_BYTES / 1024 / 1024} MB limit.`), { status: 413 });
  let parsed;
  try { parsed = parseAndProfileCsv(csvText); }
  catch (error) { throw Object.assign(new Error(error.message), { status: 400 }); }
  if (!parsed.rows.length) throw Object.assign(new Error('The CSV has a header but no data rows.'), { status: 400 });
  if (parsed.headers.includes(ROW_NUMBER_KEY)) throw Object.assign(new Error(`CSV column “${ROW_NUMBER_KEY}” is reserved for generated row IDs.`), { status: 400 });
  const sourceKey = String(input?.sourceKey ?? '');
  const timestampColumn = String(input?.timestampColumn ?? '');
  if (sourceKey !== ROW_NUMBER_KEY && !parsed.headers.includes(sourceKey)) throw Object.assign(new Error('Choose a key column from the CSV or use generated row numbers.'), { status: 400 });
  if (timestampColumn && !parsed.headers.includes(timestampColumn)) throw Object.assign(new Error('Choose a timestamp column from the CSV.'), { status: 400 });
  if (sourceKey !== ROW_NUMBER_KEY && parsed.rows.some((row) => !String(row[sourceKey]).trim())) throw Object.assign(new Error(`Key column “${sourceKey}” contains blank values.`), { status: 400 });
  if (sourceKey !== ROW_NUMBER_KEY && new Set(parsed.rows.map((row) => row[sourceKey])).size !== parsed.rows.length) throw Object.assign(new Error(`Key column “${sourceKey}” contains duplicate values.`), { status: 400 });
  if (timestampColumn && parsed.rows.some((row) => !/^\d{4}-\d{2}-\d{2}(?:T| )\d{2}:\d{2}/.test(String(row[timestampColumn]))
    || Number.isNaN(Date.parse(row[timestampColumn])))) {
    throw Object.assign(new Error(`Timestamp column “${timestampColumn}” must contain ISO-8601 date-times.`), { status: 400 });
  }
  return { id, label, fileName, mediaType: mediaType || 'text/csv', csvText, sourceKey, timestampColumn, parsed };
}

// The period column a new source of a dataset starts with: the one its other
// sources use, when this file can fill it (a survey's fixed round, T0, or a
// column it also has). Else null, and the timestamp's quarter is used.
export function siblingPeriod(mappings, headers) {
  return mappings.flatMap((m) => m?.columns ?? [])
    .find((c) => c.record === 'shared' && c.target === 'core:period'
      && (c.source.startsWith('@') || headers.includes(c.source))) ?? null;
}

// `period` replaces the timestamp's quarter as the core:period column.
export function initialSourceConfig(draft, { institution, podBase, publisher = institution, records = null, period = null }) {
  const connector = {
    id: draft.id, connector: `csv-${draft.id}-v1`, institution,
    label: publisher, sourceLabel: draft.label, sourceKind: 'csv',
    source: { kind: 'managed', ref: `managed:source/${draft.id}`, descriptor: draft.fileName },
    sourceKey: draft.sourceKey, timestampColumn: draft.timestampColumn,
    podBase, lastRun: null,
  };
  const mapping = {
    connector: draft.id, catalog: 'catalog.json',
    records: records ?? { 'core:Record': { raw: `sources/${draft.id}/` } },
    sharedDimensions: ['core:period'], columns: [
      { record: 'shared', target: 'dcterms:publisher', source: '@institution',
        transform: { fn: 'const', value: publisher } },
      ...(draft.timestampColumn ? [
        { record: 'shared', target: 'dcterms:date', source: draft.timestampColumn,
          transform: { fn: 'dateBucket', granularity: 'year' } },
      ] : []),
      ...(period ? [{ ...period }] : draft.timestampColumn ? [
        { record: 'shared', target: 'core:period', source: draft.timestampColumn,
          transform: { fn: 'dateBucket', granularity: 'quarter' } },
      ] : []),
    ], drop: [...draft.parsed.headers],
    provenance: { transformer: 'https://excentric-demo.waag.org/transformer/mapping-v1' },
  };
  return { connector, mapping };
}

// A new export of a source that is already configured: the file changes, the
// connector and mapping stay. Every column the mapping reads, and the key and
// timestamp columns, must still be in the file, or ingest would silently write
// blanks. New columns are fine: they show up as unmapped on the source's mapping screen.
export function newDataProblems(connector, mapping, headers) {
  const present = new Set(headers);
  const needed = new Set([
    ...(mapping?.columns ?? []).map((column) => column.source),
    connector?.sourceKey, connector?.timestampColumn,
  ].filter((source) => source && !source.startsWith('@')));
  const accounted = new Set([...needed, ...(mapping?.drop ?? [])]);
  return {
    missing: [...needed].filter((source) => !present.has(source)),
    added: headers.filter((header) => !accounted.has(header)),
  };
}
